import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useBranch } from "@/contexts/BranchContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ClipboardCheck, CheckCircle2, Trash2, AlertCircle, Phone, Mail, MessageCircle, MapPin, Calendar, User } from "lucide-react";
import { warn as devWarn } from "@/lib/devlog";

export default function PendingEnrollmentsPage() {
  const { selectedBranch } = useBranch();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [approving, setApproving] = useState(null); // pending doc

  const load = useCallback(async () => {
    if (!selectedBranch?.id) { setRows([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/students/pending", { params: { branch_id: selectedBranch.id } });
      setRows(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [selectedBranch?.id]);

  useEffect(() => { load(); }, [load]);

  const reject = async (id) => {
    if (!window.confirm("Reject and delete this enrollment submission?")) return;
    try {
      await api.delete(`/students/pending/${id}`);
      toast.success("Submission rejected");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <>
      <div className="space-y-6">
        <div>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h1 className="font-heading text-3xl font-bold text-foreground">Pending Enrollments</h1>
              <p className="text-sm text-brand-inkMuted mt-1">Submissions from the public enrollment QR. Review, then approve to create the student record.</p>
              {selectedBranch?.name && (
                <p className="text-xs text-brand-inkMuted mt-1.5">
                  Showing submissions for <span className="font-semibold text-foreground">{selectedBranch.name}</span>. Switch branches via the header selector above.
                </p>
              )}
            </div>
          </div>
        </div>

        {loading ? (
          <p className="text-brand-inkMuted">Loading…</p>
        ) : rows.length === 0 ? (
          <div className="bg-white rounded-3xl border border-brand-purple/10 p-10 text-center">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-brand-purpleLight/40 flex items-center justify-center mb-3">
              <ClipboardCheck className="w-7 h-7 text-brand-purple" />
            </div>
            <p className="font-heading text-lg font-semibold text-foreground">No pending enrollments</p>
            <p className="text-sm text-brand-inkMuted mt-1">Share your branch's enrollment QR to start collecting submissions.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4" data-testid="pe-list">
            {rows.map((r) => (
              <PendingCard key={r.id} row={r} onApprove={() => setApproving(r)} onReject={() => reject(r.id)} />
            ))}
          </div>
        )}
      </div>

      {approving && (
        <ApproveDialog
          row={approving}
          onClose={() => setApproving(null)}
          onApproved={() => { setApproving(null); toast.success("Student approved and enrolled"); load(); }}
        />
      )}
    </>
  );
}

function PendingCard({ row, onApprove, onReject }) {
  const g0 = (row.guardians || [])[0] || {};
  const submitted = row.submitted_at ? new Date(row.submitted_at).toLocaleString() : "—";
  return (
    <div className="bg-white rounded-3xl border border-brand-purple/10 p-5 sm:p-6" data-testid={`pe-card-${row.id}`}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <p className="font-heading text-lg font-bold text-foreground truncate">{row.full_name || row.name || "—"}</p>
          <p className="text-xs text-brand-inkMuted mt-0.5">{row.branch_name || "—"} · Submitted {submitted}</p>
        </div>
        <span className="rounded-full bg-[#FFF8E1] text-[#9A6700] border border-[#FFE082] px-2.5 py-0.5 text-[10px] font-heading uppercase tracking-wider whitespace-nowrap">
          Pending
        </span>
      </div>

      <div className="space-y-1.5 text-sm">
        <Row icon={<Calendar className="w-3.5 h-3.5" />} label="Birthday" value={row.birthday} />
        <Row icon={<MapPin className="w-3.5 h-3.5" />} label="Address" value={[row.address, row.city, row.province, row.country].filter(Boolean).join(", ")} />
        {g0.name && <Row icon={<User className="w-3.5 h-3.5" />} label="Guardian" value={g0.name} />}
        {g0.phone && <Row icon={<Phone className="w-3.5 h-3.5" />} label="Phone" value={g0.phone} />}
        {g0.email && <Row icon={<Mail className="w-3.5 h-3.5" />} label="Email" value={g0.email} />}
        {g0.fb_messenger && <Row icon={<MessageCircle className="w-3.5 h-3.5" />} label="Messenger" value={g0.fb_messenger} />}
        {row.notes_from_parent && (
          <div className="mt-2 rounded-xl bg-[#F6F8FE] p-3 text-xs text-brand-inkMuted">
            <p className="font-heading uppercase tracking-wider text-[10px] text-brand-purple mb-1">Notes from parent</p>
            {row.notes_from_parent}
          </div>
        )}
      </div>

      <div className="mt-4 flex items-center gap-2 justify-end">
        <Button onClick={onReject} variant="outline" className="rounded-2xl text-[#C62828] border-[#FDECEA] hover:bg-[#FDECEA]" data-testid={`pe-reject-${row.id}`}>
          <Trash2 className="w-4 h-4 mr-1.5" /> Reject
        </Button>
        <Button onClick={onApprove} className="rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white" data-testid={`pe-approve-${row.id}`}>
          <CheckCircle2 className="w-4 h-4 mr-1.5" /> Approve & Enroll
        </Button>
      </div>
    </div>
  );
}
function Row({ icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 text-sm">
      <span className="text-brand-inkMuted mt-0.5">{icon}</span>
      <span className="text-brand-inkMuted shrink-0">{label}:</span>
      <span className="font-medium text-foreground break-words">{value}</span>
    </div>
  );
}

function ApproveDialog({ row, onClose, onApproved }) {
  const [classes, setClasses] = useState([]);     // regular classes (kind=class)
  const [individuals, setIndividuals] = useState([]); // individual classes
  const [sections, setSections] = useState([]);

  const [enrollClass, setEnrollClass] = useState(false);
  const [classId, setClassId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [enrollIndiv, setEnrollIndiv] = useState(false);
  const [individualClassId, setIndividualClassId] = useState("");
  const [tuitionFee, setTuitionFee] = useState("0");
  const [modeOfPayment, setModeOfPayment] = useState("Monthly");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/classes", { params: { branch_id: row.branch_id } });
        const list = data || [];
        setClasses(list.filter((c) => (c.class_type || "regular") !== "individual"));
        setIndividuals(list.filter((c) => c.class_type === "individual"));
      } catch (e) { devWarn(e); }
    })();
  }, [row.branch_id]);

  useEffect(() => {
    if (!classId) { setSections([]); return; }
    (async () => {
      try {
        const { data } = await api.get("/sections", { params: { branch_id: row.branch_id, class_id: classId } });
        setSections(data || []);
      } catch (e) { devWarn(e); }
    })();
  }, [classId, row.branch_id]);

  const canSave = useMemo(() => {
    if (!enrollClass && !enrollIndiv) return true; // approval without assignment is allowed
    if (enrollClass && (!classId || !sectionId)) return false;
    if (enrollIndiv && !individualClassId) return false;
    return true;
  }, [enrollClass, classId, sectionId, enrollIndiv, individualClassId]);

  const approve = async () => {
    if (!canSave) { toast.error("Please complete the class/section selection"); return; }
    setSaving(true);
    try {
      await api.post(`/students/pending/${row.id}/approve`, {
        enroll_in_class: enrollClass,
        class_id: enrollClass ? classId : null,
        section_id: enrollClass ? sectionId : null,
        enroll_in_individual: enrollIndiv,
        individual_class_id: enrollIndiv ? individualClassId : null,
        tuition_fee: Number(tuitionFee || 0),
        mode_of_payment: modeOfPayment,
      });
      onApproved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg rounded-3xl" data-testid="pe-approve-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Approve {row.full_name || row.name}</DialogTitle>
          <DialogDescription>Pick the class/section to assign. You can enroll in regular class, individual class, or both — or approve without assignment to do it later.</DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="rounded-2xl border border-brand-purple/15 bg-[#F6F8FE]/40 p-4 space-y-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={enrollClass} onChange={(e) => setEnrollClass(e.target.checked)} className="w-4 h-4 accent-[#5D2E8E]" data-testid="pe-enroll-class-toggle" />
              <span className="font-heading font-semibold">Enroll in a Regular Class</span>
            </label>
            {enrollClass && (
              <div className="grid sm:grid-cols-2 gap-3 pl-6">
                <div>
                  <Label className="font-heading text-xs uppercase tracking-wider text-brand-inkMuted">Class</Label>
                  <select value={classId} onChange={(e) => { setClassId(e.target.value); setSectionId(""); }} className="mt-1.5 w-full h-11 rounded-2xl border border-input bg-white px-3 text-sm" data-testid="pe-class-select">
                    <option value="">Pick a class…</option>
                    {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="font-heading text-xs uppercase tracking-wider text-brand-inkMuted">Section</Label>
                  <select value={sectionId} onChange={(e) => setSectionId(e.target.value)} disabled={!classId} className="mt-1.5 w-full h-11 rounded-2xl border border-input bg-white px-3 text-sm disabled:opacity-50" data-testid="pe-section-select">
                    <option value="">Pick a section…</option>
                    {sections.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-brand-purple/15 bg-[#F6F8FE]/40 p-4 space-y-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={enrollIndiv} onChange={(e) => setEnrollIndiv(e.target.checked)} className="w-4 h-4 accent-[#5D2E8E]" data-testid="pe-enroll-individual-toggle" />
              <span className="font-heading font-semibold">Enroll in an Individual Session</span>
            </label>
            {enrollIndiv && (
              <div className="pl-6">
                <Label className="font-heading text-xs uppercase tracking-wider text-brand-inkMuted">Individual Class</Label>
                <select value={individualClassId} onChange={(e) => setIndividualClassId(e.target.value)} className="mt-1.5 w-full h-11 rounded-2xl border border-input bg-white px-3 text-sm" data-testid="pe-individual-class-select" disabled={individuals.length === 0}>
                  <option value="">{individuals.length === 0 ? "No individual classes available in this branch" : "Pick an individual class…"}</option>
                  {individuals.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                {individuals.length === 0 ? (
                  <p className="text-[11px] text-[#C62828] mt-1">Create an Individual Session class for this branch first (Classes & Sections → Add Class → Type: Individual).</p>
                ) : (
                  <p className="text-[11px] text-brand-inkMuted mt-1">Only classes flagged as "Individual" appear here.</p>
                )}
              </div>
            )}
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="font-heading text-xs uppercase tracking-wider text-brand-inkMuted">Tuition Fee</Label>
              <Input type="number" min="0" step="0.01" value={tuitionFee} onChange={(e) => setTuitionFee(e.target.value)} className="rounded-2xl h-11 mt-1.5" data-testid="pe-tuition-fee" />
            </div>
            <div>
              <Label className="font-heading text-xs uppercase tracking-wider text-brand-inkMuted">Mode of Payment</Label>
              <select value={modeOfPayment} onChange={(e) => setModeOfPayment(e.target.value)} className="mt-1.5 w-full h-11 rounded-2xl border border-input bg-white px-3 text-sm" data-testid="pe-mode-payment">
                {["Monthly", "Semestral", "Per Session", "Quarterly", "Annual", "Other"].map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          </div>

          {!enrollClass && !enrollIndiv && (
            <div className="rounded-2xl bg-[#FFF8E1] border border-[#FFE082] p-3 flex gap-2 text-xs text-[#5D4037]">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-[#E65100]" />
              You're approving without assigning a class. You can assign one later from the student's profile.
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-2xl h-11">Cancel</Button>
          <Button onClick={approve} disabled={saving || !canSave} className="rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white h-11" data-testid="pe-approve-submit">
            {saving ? "Approving…" : "Approve & Enroll"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
